import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// Return stop for Emergent-managed Google sign-in: auth.emergentagent.com lands
// back here with a one-time session_id in the URL fragment. The backend swaps it
// for a Supabase magic-link token, and verifyOtp turns that into a normal
// Supabase session — AuthContext picks it up the same way it does for
// email/password sign-in, so nothing else about session handling changes.
//
// REMINDER: DO NOT HARDCODE A DIFFERENT URL OR ADD FALLBACK REDIRECTS HERE.
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  // StrictMode double-invokes effects; the exchange must run exactly once.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    if (!sessionId) {
      setError("That sign-in link was missing its token — please try again.");
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) {
          let detail = "Google sign-in failed or the link expired — please try again.";
          try {
            detail = (await res.json())?.detail || detail;
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }
        const { token_hash } = await res.json();
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash,
          type: "email",
        });
        if (verifyError) throw verifyError;
        navigate("/", { replace: true });
      } catch (err) {
        setError(err?.message || "Google sign-in failed or the link expired — please try again.");
      }
    })();
  }, [location.hash, navigate]);

  return (
    <div className="flex min-h-dvh flex-1 items-center justify-center bg-background p-4">
      {error ? (
        <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 text-center shadow-xl">
          <h1 className="text-xl font-bold">Sign-in trouble</h1>
          <p data-testid="auth-callback-error" className="mt-2 text-sm text-muted-foreground">
            {error}
          </p>
          <Link
            to="/login"
            data-testid="auth-callback-back-link"
            className="mt-6 inline-block rounded-full border border-border px-4 py-2 text-sm font-semibold"
          >
            Back to login
          </Link>
        </div>
      ) : (
        <div data-testid="auth-callback-processing" className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm font-semibold text-muted-foreground">Signing you in…</p>
        </div>
      )}
    </div>
  );
}

